/**
 * MONSTER TOWN — THE PLACEMENT MODEL. FROZEN CONTRACT (src/core/*). Additive only.
 *
 * ONE model for everything the kid builds. Town, house exterior, house interior
 * and venue interiors are all the same `Grid`, differing only by `tileSize` and
 * `levelHeight`. That is why a furniture piece and a tree use identical code.
 *
 * AXES (memorised, never re-derived):
 *   +X = EAST, -X = WEST, +Z = SOUTH, -Z = NORTH, +Y = UP.
 *   Cell (gx,gz) covers world x in [gx*t, (gx+1)*t) and z in [gz*t, (gz+1)*t).
 *   Cell CENTRE is ((gx+0.5)*t, gy*levelHeight, (gz+0.5)*t).
 *   `gy` is a HEIGHT LEVEL (integer), not a world Y. Level 0 sits ON the ground.
 *
 * ROTATION is `Rot` = 0|1|2|3 quarter turns CLOCKWISE seen from above
 *   (rot 1 = 90deg, sends +X -> +Z). Footprints rotate with the piece.
 *
 * WALLS ARE TILE-BASED, NOT EDGE-BASED. A wall occupies a whole cell. This is a
 * deliberate simplification for 3-6 year olds: an edge model needs precision a
 * fat finger does not have, and tile walls auto-corner for free via connect
 * masks. Do not "fix" this.
 *
 * AUTO-CONNECT CONTRACT (LAW #5 — kids cannot build something ugly):
 *   1. A def may declare `connect: { group, style }`.
 *   2. On place/remove, the build system MUST call `grid.refreshSet(cells)` and
 *      rebuild the art of every returned placement.
 *   3. An art builder receives `ctx.connect`, a 4-bit mask (N=1,E=2,S=4,W=8) of
 *      which orthogonal neighbours share its connect group, and `ctx.connect8`,
 *      an 8-bit mask that adds the diagonals (NE=16,SE=32,SW=64,NW=128) for
 *      inner-corner art.
 *   4. The art builder MUST look identical for the same mask every time — the
 *      renderer may rebuild at any moment.
 */

import type { InstanceId, PlaceableId, Vec3 } from './types'

// ---------------------------------------------------------------------------
// primitives
// ---------------------------------------------------------------------------

export type Rot = 0 | 1 | 2 | 3

export interface Cell {
  readonly gx: number
  readonly gy: number
  readonly gz: number
}

/** Size in tiles (w = along X at rot 0, d = along Z at rot 0) and levels tall. */
export interface Footprint {
  readonly w: number
  readonly d: number
  readonly h: number
}

export const FOOT_1x1: Footprint = { w: 1, d: 1, h: 1 }
export const FOOT_2x1: Footprint = { w: 2, d: 1, h: 1 }
export const FOOT_2x2: Footprint = { w: 2, d: 2, h: 1 }
export const FOOT_3x2: Footprint = { w: 3, d: 2, h: 1 }
export const FOOT_WALL: Footprint = { w: 1, d: 1, h: 2 }

export const DIR = { N: 0, E: 1, S: 2, W: 3 } as const
export type Dir = (typeof DIR)[keyof typeof DIR]

export const MASK = {
  N: 1,
  E: 2,
  S: 4,
  W: 8,
  NE: 16,
  SE: 32,
  SW: 64,
  NW: 128,
} as const

const ORTHO: readonly (readonly [number, number])[] = [
  [0, -1], // N
  [1, 0], // E
  [0, 1], // S
  [-1, 0], // W
]
const DIAG: readonly (readonly [number, number])[] = [
  [1, -1], // NE
  [1, 1], // SE
  [-1, 1], // SW
  [-1, -1], // NW
]

/** Scale profiles. Interiors use a finer tile so furniture reads at doll scale. */
export const GRID_SCALE = {
  /** Town / house exterior: 1 tile = 2 world units, a level is 2 units tall. */
  exterior: { tileSize: 2, levelHeight: 2 },
  /** Interiors: 1 tile = 1 world unit, a level is 1.4 units tall. */
  interior: { tileSize: 1, levelHeight: 1.4 },
} as const

export type GridScale = keyof typeof GRID_SCALE

export interface GridSpec {
  readonly id: string
  readonly scale: GridScale
  readonly width: number
  readonly depth: number
  readonly levels: number
  /** World-space offset of cell (0,0,0)'s minimum corner. Usually centres the plot. */
  readonly originX?: number
  readonly originZ?: number
}

/** What actually sits in the grid. Art is NOT stored here — this is pure data. */
export interface Placement {
  readonly instanceId: InstanceId
  readonly defId: PlaceableId
  readonly anchor: Cell
  readonly rot: Rot
  readonly footprint: Footprint
  /** Copied from the def so the grid never has to reach into the registry. */
  readonly connectGroup: string | null
  /** Copied from the def: can other pieces be stacked on top of this one? */
  readonly stackable: boolean
  /** Copied from the def: can a monster walk over this cell? */
  readonly walkable: boolean
}

export type PlaceCheck =
  | 'ok'
  | 'outOfBounds'
  | 'occupied'
  | 'noSupport'
  | 'notStackable'
  | 'tooTall'

export interface PlaceQuery {
  readonly anchor: Cell
  readonly footprint: Footprint
  readonly rot: Rot
  /** Ignore this instance when testing (used while dragging an existing piece). */
  readonly ignore?: InstanceId
  /** Level 0 pieces may sit on bare ground; higher ones need support beneath. */
  readonly requiresSupport?: boolean
}

// ---------------------------------------------------------------------------
// Grid
// ---------------------------------------------------------------------------

export class Grid {
  readonly spec: GridSpec
  readonly tileSize: number
  readonly levelHeight: number
  readonly originX: number
  readonly originZ: number

  /** cellKey -> instanceId */
  private readonly cells = new Map<string, InstanceId>()
  /** instanceId -> Placement */
  private readonly placements = new Map<InstanceId, Placement>()

  constructor(spec: GridSpec) {
    this.spec = spec
    const s = GRID_SCALE[spec.scale]
    this.tileSize = s.tileSize
    this.levelHeight = s.levelHeight
    this.originX = spec.originX ?? -(spec.width * s.tileSize) / 2
    this.originZ = spec.originZ ?? -(spec.depth * s.tileSize) / 2
  }

  // -- keys / bounds ------------------------------------------------------

  static key(c: Cell): string {
    return `${c.gx}|${c.gy}|${c.gz}`
  }

  inBounds(c: Cell): boolean {
    return (
      c.gx >= 0 &&
      c.gz >= 0 &&
      c.gy >= 0 &&
      c.gx < this.spec.width &&
      c.gz < this.spec.depth &&
      c.gy < this.spec.levels
    )
  }

  // -- world <-> grid -----------------------------------------------------

  /** World point -> the cell containing it, at the given height level. */
  worldToCell(x: number, z: number, gy = 0): Cell {
    return {
      gx: Math.floor((x - this.originX) / this.tileSize),
      gy,
      gz: Math.floor((z - this.originZ) / this.tileSize),
    }
  }

  /** Cell -> world-space CENTRE of that single cell, y = floor of the level. */
  cellToWorld(c: Cell): Vec3 {
    return {
      x: this.originX + (c.gx + 0.5) * this.tileSize,
      y: c.gy * this.levelHeight,
      z: this.originZ + (c.gz + 0.5) * this.tileSize,
    }
  }

  /**
   * World-space centre of a whole FOOTPRINT anchored at `anchor`. This is where
   * art must be positioned — a 2x2 piece sits between cells, not on one.
   */
  footprintCenter(anchor: Cell, fp: Footprint, rot: Rot): Vec3 {
    const { w, d } = rotatedSize(fp, rot)
    return {
      x: this.originX + (anchor.gx + w / 2) * this.tileSize,
      y: anchor.gy * this.levelHeight,
      z: this.originZ + (anchor.gz + d / 2) * this.tileSize,
    }
  }

  /** World Y of the floor of a level. */
  levelY(gy: number): number {
    return gy * this.levelHeight
  }

  /**
   * SNAP RULE. Given the world point the finger is over, return the anchor cell
   * that puts the footprint CENTRED under the finger. This is the only snap the
   * game uses — no sub-tile positioning, ever. LAW #5.
   */
  snapAnchor(x: number, z: number, fp: Footprint, rot: Rot, gy = 0): Cell {
    const { w, d } = rotatedSize(fp, rot)
    const fx = (x - this.originX) / this.tileSize
    const fz = (z - this.originZ) / this.tileSize
    return {
      gx: Math.round(fx - w / 2),
      gy,
      gz: Math.round(fz - d / 2),
    }
  }

  /** Clamp an anchor so the whole footprint stays inside the plot. */
  clampAnchor(anchor: Cell, fp: Footprint, rot: Rot): Cell {
    const { w, d } = rotatedSize(fp, rot)
    return {
      gx: Math.min(Math.max(anchor.gx, 0), Math.max(0, this.spec.width - w)),
      gy: Math.min(Math.max(anchor.gy, 0), Math.max(0, this.spec.levels - fp.h)),
      gz: Math.min(Math.max(anchor.gz, 0), Math.max(0, this.spec.depth - d)),
    }
  }

  // -- footprints ---------------------------------------------------------

  /** Every cell a placement occupies, including all height levels. */
  footprintCells(anchor: Cell, fp: Footprint, rot: Rot): Cell[] {
    const { w, d } = rotatedSize(fp, rot)
    const out: Cell[] = []
    for (let dy = 0; dy < fp.h; dy++) {
      for (let dz = 0; dz < d; dz++) {
        for (let dx = 0; dx < w; dx++) {
          out.push({ gx: anchor.gx + dx, gy: anchor.gy + dy, gz: anchor.gz + dz })
        }
      }
    }
    return out
  }

  /** Only the base (gy = anchor.gy) cells — the ones that need support. */
  baseCells(anchor: Cell, fp: Footprint, rot: Rot): Cell[] {
    const { w, d } = rotatedSize(fp, rot)
    const out: Cell[] = []
    for (let dz = 0; dz < d; dz++) {
      for (let dx = 0; dx < w; dx++) {
        out.push({ gx: anchor.gx + dx, gy: anchor.gy, gz: anchor.gz + dz })
      }
    }
    return out
  }

  // -- occupancy ----------------------------------------------------------

  at(c: Cell): InstanceId | null {
    return this.cells.get(Grid.key(c)) ?? null
  }

  placementAt(c: Cell): Placement | null {
    const id = this.at(c)
    return id === null ? null : (this.placements.get(id) ?? null)
  }

  placement(id: InstanceId): Placement | null {
    return this.placements.get(id) ?? null
  }

  all(): Placement[] {
    return [...this.placements.values()]
  }

  count(): number {
    return this.placements.size
  }

  /** Lowest free level at (gx,gz) — where a stacked piece would land. */
  topLevelAt(gx: number, gz: number): number {
    let gy = 0
    while (gy < this.spec.levels && this.cells.has(Grid.key({ gx, gy, gz }))) gy++
    return gy
  }

  /** Full validity check. Returns 'ok' or the reason a kid can't put it there. */
  canPlace(q: PlaceQuery): PlaceCheck {
    const cells = this.footprintCells(q.anchor, q.footprint, q.rot)
    for (const c of cells) {
      if (!this.inBounds(c)) return c.gy >= this.spec.levels ? 'tooTall' : 'outOfBounds'
      const occ = this.cells.get(Grid.key(c))
      if (occ !== undefined && occ !== q.ignore) return 'occupied'
    }
    if (q.anchor.gy > 0 && q.requiresSupport !== false) {
      for (const c of this.baseCells(q.anchor, q.footprint, q.rot)) {
        const below = this.placementAt({ gx: c.gx, gy: c.gy - 1, gz: c.gz })
        if (below === null || below.instanceId === q.ignore) return 'noSupport'
        if (!below.stackable) return 'notStackable'
      }
    }
    return 'ok'
  }

  /**
   * Commit a placement. Caller must have checked `canPlace` first; this throws
   * on conflict so bugs surface loudly in dev rather than corrupting a town.
   */
  occupy(p: Placement): void {
    const cells = this.footprintCells(p.anchor, p.footprint, p.rot)
    for (const c of cells) {
      const k = Grid.key(c)
      const occ = this.cells.get(k)
      if (occ !== undefined && occ !== p.instanceId) {
        throw new Error(`grid.occupy: cell ${k} already held by ${occ}`)
      }
    }
    this.release(p.instanceId)
    for (const c of cells) this.cells.set(Grid.key(c), p.instanceId)
    this.placements.set(p.instanceId, p)
  }

  /** Remove a placement. Safe to call for an id that is not present. */
  release(id: InstanceId): Placement | null {
    const p = this.placements.get(id)
    if (p === undefined) return null
    for (const c of this.footprintCells(p.anchor, p.footprint, p.rot)) {
      if (this.cells.get(Grid.key(c)) === id) this.cells.delete(Grid.key(c))
    }
    this.placements.delete(id)
    return p
  }

  clear(): void {
    this.cells.clear()
    this.placements.clear()
  }

  // -- walkability (play mode) --------------------------------------------

  /** Can a monster stand on this ground cell? Blocked by non-walkable level-0 pieces. */
  isWalkable(gx: number, gz: number): boolean {
    if (!this.inBounds({ gx, gy: 0, gz })) return false
    const p = this.placementAt({ gx, gy: 0, gz })
    return p === null || p.walkable
  }

  isWorldWalkable(x: number, z: number): boolean {
    const c = this.worldToCell(x, z, 0)
    return this.isWalkable(c.gx, c.gz)
  }

  // -- auto-connect -------------------------------------------------------

  neighborCell(c: Cell, dir: Dir): Cell {
    const d = ORTHO[dir] as readonly [number, number]
    return { gx: c.gx + d[0], gy: c.gy, gz: c.gz + d[1] }
  }

  /** 4-bit N/E/S/W mask of neighbours sharing `group`. */
  connectMask(c: Cell, group: string): number {
    let m = 0
    for (let i = 0; i < 4; i++) {
      const d = ORTHO[i] as readonly [number, number]
      const p = this.placementAt({ gx: c.gx + d[0], gy: c.gy, gz: c.gz + d[1] })
      if (p !== null && p.connectGroup === group) m |= 1 << i
    }
    return m
  }

  /** 8-bit mask: orthogonals in bits 0-3, diagonals NE/SE/SW/NW in bits 4-7. */
  connectMask8(c: Cell, group: string): number {
    let m = this.connectMask(c, group)
    for (let i = 0; i < 4; i++) {
      const d = DIAG[i] as readonly [number, number]
      const p = this.placementAt({ gx: c.gx + d[0], gy: c.gy, gz: c.gz + d[1] })
      if (p !== null && p.connectGroup === group) m |= 1 << (4 + i)
    }
    return m
  }

  /**
   * THE refresh query. Given the cells that just changed, return every placement
   * whose connect art may now be stale (itself + all 8 neighbours, all levels
   * touched). Build system calls this after every occupy/release.
   */
  refreshSet(changed: readonly Cell[]): Placement[] {
    const seen = new Set<InstanceId>()
    const out: Placement[] = []
    const push = (c: Cell): void => {
      const p = this.placementAt(c)
      if (p === null || p.connectGroup === null || seen.has(p.instanceId)) return
      seen.add(p.instanceId)
      out.push(p)
    }
    for (const c of changed) {
      push(c)
      for (const d of ORTHO) push({ gx: c.gx + d[0], gy: c.gy, gz: c.gz + d[1] })
      for (const d of DIAG) push({ gx: c.gx + d[0], gy: c.gy, gz: c.gz + d[1] })
    }
    return out
  }
}

// ---------------------------------------------------------------------------
// free functions
// ---------------------------------------------------------------------------

/** Footprint size after rotation. Odd turns swap w and d. */
export function rotatedSize(fp: Footprint, rot: Rot): { w: number; d: number } {
  return rot === 1 || rot === 3 ? { w: fp.d, d: fp.w } : { w: fp.w, d: fp.d }
}

/** Yaw in radians for a Rot, for `object.rotation.y`. Clockwise from above. */
export function rotYaw(rot: Rot): number {
  return (-rot * Math.PI) / 2
}

export function nextRot(rot: Rot): Rot {
  return ((rot + 1) % 4) as Rot
}

export function cellEquals(a: Cell, b: Cell): boolean {
  return a.gx === b.gx && a.gy === b.gy && a.gz === b.gz
}

/** Rotate a connect mask by `rot` quarter turns so art can be built once. */
export function rotateMask(mask: number, rot: Rot): number {
  const ortho = mask & 0b1111
  const diag = (mask >> 4) & 0b1111
  const ro = ((ortho << rot) | (ortho >> (4 - rot))) & 0b1111
  const rd = ((diag << rot) | (diag >> (4 - rot))) & 0b1111
  return ro | (rd << 4)
}

/** Manhattan distance in tiles, ignoring level. */
export function cellDist(a: Cell, b: Cell): number {
  return Math.abs(a.gx - b.gx) + Math.abs(a.gz - b.gz)
}
